import { Component, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DirectorSearchBoxComponent } from '../director-search-box/director-search-box.component';
import { DirectorSearchFilter } from '../../../models/directorSearchFilter';
import { DirectorFlat } from '../../../models/director';
import { ColumnDefinition, ListItemClickEvent } from '../../../models/columnDefinition';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { DirectorService } from '../../../services/director.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Store } from '@ngrx/store';
import { IAppState } from '../../../store/states/app.state';
import { SetSearchFilter, SetPagination, SetSort } from '../../../store/actions/director-list.actions';
import { selectDirectorListFullState } from '../../../store/selectors/director-list.selectors';
import { take, filter } from 'rxjs/operators';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-director-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    DirectorSearchBoxComponent,
    GenericListComponent,
    NgxSpinnerModule
  ],
  templateUrl: './director-list.component.html',
  styleUrl: './director-list.component.scss'
})
export class DirectorListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(GenericListComponent) genericList?: GenericListComponent<DirectorFlat>;

  directors: DirectorFlat[] = [];
  totalItems = 0;
  isSearchVisible = true;

  // Pagination and sorting state
  currentPage = 1;
  pageSize = 10;
  sortBy = 'fullName';
  ascending = true;
  currentSearchFilter: DirectorSearchFilter = {};

  // Merge functionality state
  rightItem: DirectorFlat | undefined = undefined;
  matchedItems: DirectorFlat[] | undefined = undefined;

  showBoardMemberships = false;

  private allColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'code', displayName: 'Internal ID' },
    { propertyName: 'fullName', displayName: 'Full Name' },
    { propertyName: 'boardMembershipDisplay', displayName: 'Board Memberships', dataType: 'list', listClickable: true },
    //{ propertyName: 'yearOfBirth', displayName: 'Year of Birth' },
    //{ propertyName: 'honorificTitleName', displayName: 'Title' },
    { propertyName: 'nationalityName', displayName: 'Nationality' },
    { propertyName: 'professionName', displayName: 'Profession' },
    //{ propertyName: 'qualificationName', displayName: 'Qualification' },
    { propertyName: 'isCompleted', displayName: 'Completed', dataType: 'boolean' }
  ];

  get columnDefinitions(): ColumnDefinition[] {
    if (this.showBoardMemberships) {
      return this.allColumnDefinitions;
    }
    return this.allColumnDefinitions.filter(c => c.propertyName !== 'boardMembershipDisplay');
  }

  constructor(
    private router: Router,
    private directorService: DirectorService,
    private toast: HotToastService,
    private spinnerService: NgxSpinnerService,
    private store: Store<IAppState>,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    // Restore state from store
    this.store.select(selectDirectorListFullState).pipe(
      filter(state => state !== undefined && state !== null),
      take(1)
    ).subscribe(state => {
      this.currentSearchFilter = state.searchFilter;
      this.currentPage = state.currentPage;
      this.pageSize = state.pageSize;
      this.sortBy = state.sortBy;
      this.ascending = state.ascending;

      // Load directors with restored state
      this.loadDirectors();
    });
  }

  ngAfterViewInit(): void {
    // Paginator synchronization is handled by GenericListComponent
  }

  private loadDirectors(): void {
    this.spinnerService.show();

    const searchPayload = {
      ...this.currentSearchFilter,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    };

    this.directorService.searchDirectors(searchPayload).subscribe({
      next: (response) => {
        this.directors = response.data.map(director => ({
          ...director,
          boardMembershipDisplay: (director.boardMemberships || [])
            .sort((a, b) => a.companyName.localeCompare(b.companyName) || a.year - b.year)
            .map(m => ({ label: `${m.companyName} (${m.year})`, companyId: m.companyId, year: m.year }))
        }));
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading directors:', error);
        this.toast.error('Error loading directors');
        this.spinnerService.hide();
      }
    });
  }

  async onListItemClick(event: ListItemClickEvent<DirectorFlat>): Promise<void> {
    if (event.column === 'boardMembershipDisplay' && event.listItem?.companyId) {
      try {
        await this.router.navigate(['/companies/edit', event.listItem.companyId], {
          queryParams: { tab: 'board', year: event.listItem.year }
        });
      } catch (error) {
        this.toast.error('Error navigating to Company edit (route not found)');
      }
    }
  }

  async onEditDirector(director: DirectorFlat): Promise<void> {
    try {
      await this.router.navigate(['/directors/edit', director.id]);
    } catch (error) {
      this.toast.error('Error navigating to Director edit (route not found)');
    }
  }

  onOpenDirectorInNewTab(director: DirectorFlat): void {
    const urlTree = this.router.createUrlTree(['/directors/edit', director.id]);
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank');
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.ascending = sort.direction === 'asc';

    // Update store
    this.store.dispatch(new SetSort({
      sortBy: this.sortBy,
      ascending: this.ascending
    }));

    // Reload data
    this.loadDirectors();
  }

  onPageChange(event: PageEvent): void {
    // Update pagination state (API uses 1-based indexing)
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;

    // Update store
    this.store.dispatch(new SetPagination({
      currentPage: this.currentPage,
      pageSize: this.pageSize
    }));

    // Reload data
    this.loadDirectors();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.currentSearchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onDirectorSearchFilterChange(directorSearchFilter: DirectorSearchFilter): void {
    this.currentSearchFilter = directorSearchFilter;
    this.currentPage = 1; // Reset to first page on new search

    // Update store
    this.store.dispatch(new SetSearchFilter(directorSearchFilter));

    // Reset paginator UI to first page
    if (this.genericList?.paginator) {
      this.genericList.paginator.pageIndex = 0;
    }

    // Reload data
    this.loadDirectors();
  }

  onExportToCsv(): void {
    this.spinnerService.show();

    this.directorService.exportDirectorsToCsv({
      ...this.currentSearchFilter,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `directors_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.toast.success('Directors exported successfully');
      },
      error: (error) => {
        console.error('Error exporting directors:', error);
        this.toast.error('Error exporting directors');
        this.spinnerService.hide();
      }
    });
  }

  onReadyToMatch(event: {rightItem?: DirectorFlat, matchedItems?: DirectorFlat[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onDeleteDirector(director: DirectorFlat): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(director.fullName)
      .subscribe(result => {
        if (result) {
          // Store original lists for rollback in case of error
          const originalDirectors = this.directors;
          const originalTotalItems = this.totalItems;

          // 1. Optimistically remove from local state immediately
          this.directors = this.directors.filter(d => d.id !== director.id);
          this.totalItems = this.totalItems - 1;

          // 2. Call backend API
          this.spinnerService.show();
          this.directorService.deleteDirector(director.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Director deleted successfully');
              // 3. On success: Reload the list to ensure consistency with backend
              this.loadDirectors();
            },
            error: (error) => {
              console.error('Error deleting director:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original lists
              this.directors = originalDirectors;
              this.totalItems = originalTotalItems;
              this.toast.error(`<strong>Failed to delete Director:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onDirectorCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select directors to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.fullName).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.fullName}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original lists for rollback in case of error
        const originalDirectors = this.directors;
        const originalTotalItems = this.totalItems;

        // 1. Optimistically remove merged items from local state
        this.directors = this.directors.filter(d => !bulkMergeRequest.leftIds.includes(d.id));
        this.totalItems = this.totalItems - bulkMergeRequest.leftIds.length;

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.directorService.bulkMergeDirectors(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Directors merged successfully');
            // 3. On success: Clear the generic list's internal matching state and reload the list
            this.genericList?.clearMatchingState();
            this.loadDirectors();
          },
          error: (error) => {
            console.error('Error merging directors:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original lists
            this.directors = originalDirectors;
            this.totalItems = originalTotalItems;
            // Restore selection state for retry
            this.rightItem = originalDirectors.find(d => d.id === bulkMergeRequest.rightId);
            this.matchedItems = originalDirectors.filter(d => bulkMergeRequest.leftIds.includes(d.id));
            this.toast.error(`<strong>Failed to merge Directors:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    // Component cleanup
  }
}
